// src/context/DayBoundaryContext.js
// Firestore appConfig/dayBoundary から日付切り替え時刻（JST）を取得し、
// アプリ全体で共有する Context。
//
// 使い方:
//   const { hour, minute, buildDateKey } = useDayBoundary();
//   const todayKey = buildDateKey(new Date());

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../api/firebase";

const DayBoundaryContext = createContext({
  hour: 3,
  minute: 0,
  buildDateKey: () => "",
});

export function DayBoundaryProvider({ children }) {
  const [boundary, setBoundary] = useState({ hour: 3, minute: 0 });

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "appConfig", "dayBoundary"),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setBoundary({
          hour: typeof data.hour === "number" ? data.hour : 3,
          minute: typeof data.minute === "number" ? data.minute : 0,
        });
      },
      (err) => {
        console.error("[DayBoundaryContext] onSnapshot error:", err);
      }
    );
    return () => unsub();
  }, []);

  const buildDateKey = useCallback(
    (date) => {
      const offsetMs = (boundary.hour * 60 + boundary.minute) * 60 * 1000;
      const jstMs = date.getTime() + 9 * 60 * 60 * 1000;
      const shifted = new Date(jstMs - offsetMs);
      const y = shifted.getUTCFullYear();
      const m = String(shifted.getUTCMonth() + 1).padStart(2, "0");
      const d = String(shifted.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    [boundary.hour, boundary.minute]
  );

  return (
    <DayBoundaryContext.Provider value={{ ...boundary, buildDateKey }}>
      {children}
    </DayBoundaryContext.Provider>
  );
}

export function useDayBoundary() {
  return useContext(DayBoundaryContext);
}
